import {AfterContentInit, Component, Input, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {combineLatestAll, of, Subject} from 'rxjs';
import {first, takeUntil} from 'rxjs/operators';
import {
    GridColumn,
    IdNameMxObj,
    KeyValueObj,
    MxButtonModel,
    MxGridButtonsConfig,
    MxGridComponent,
    MxGridCustomizerEvent,
    MxGridHandleOrderDefault,
    MxGridService,
    MxInformationTooltipConfig,
    MxPreloaderService,
    PreloaderConfig,
    withPreloader
} from 'mx-core';

import {MOMENT_DATE_FORMAT_DAY} from '../../../../../../../mx-core/src/lib/consts/date-time-format-consts';

import {PrlFinanceService} from '../../services/prl-finance.service';
import {PrlTranslateService} from '../../../../shared/services/prl-translate.service';
import {PrlTransactionsService} from '../../services/prl-transactions.service';
import {TransactionPrlObj, TransactionsSearchTypeObj} from '../../../../../../generated/generated.model';
import {PrlLanguageService} from '../../../prl-language/services/prl-language.service';


@Component({
    selector: 'prl-payment-history',
    templateUrl: './prl-payment-history.component.html',
    standalone: false,
    styleUrls: ['./prl-payment-history.component.scss']
})
export class PrlPaymentHistoryComponent implements OnInit, AfterContentInit, OnDestroy {
    @Input() balancePerMerchant: boolean = false;

    public isDataReady: boolean = false;

    protected form: FormGroup;
    protected selectedDateRangeControl: FormControl = new FormControl('', Validators.required);
    protected selectedMerchantControl: FormControl = new FormControl('', Validators.required);
    protected selectedTransactionTypeControl: FormControl = new FormControl('', Validators.required);

    protected runReportButton: MxButtonModel;
    protected exportReportButton: MxButtonModel;

    protected exportReportConfig: MxInformationTooltipConfig = {
        links: [],
        header: 'Help',
        content: 'label.export.to.csv.help'
    };

    protected merchants: Array<KeyValueObj> = [];
    protected transactionTypes: Array<KeyValueObj> = [];
    protected dateRanges: Array<KeyValueObj> = [];

    protected gettingData: boolean = true;
    protected firstLoadReport: boolean = true;

    private key: string = 'key';
    private gridColumns: GridColumn[] = [];
    private gridService: MxGridService;
    private isShowBalance = false;

    @ViewChild(MxGridComponent, {static: true})
    private grid: MxGridComponent;

    private destroy$$: Subject<null> = new Subject<null>();
    private preloaderConfig: PreloaderConfig = {name: 'report', type: 'LinearSpinner'};

    constructor(
        private financeService: PrlFinanceService,
        private languageService: PrlLanguageService,
        private preloader: MxPreloaderService,
        private transactionsService: PrlTransactionsService,
        private translateService: PrlTranslateService,
    ) {
        this.form = new FormGroup({});
        this.form.addControl('type', this.selectedTransactionTypeControl);
        this.form.addControl('dateRange', this.selectedDateRangeControl);
        this.form.addControl('merchant', this.selectedMerchantControl);
    }

    ngOnDestroy(): void {
        this.destroy$$.next(null);
    }

    ngOnInit(): void {
        this.runReportButton = {
            label: 'label.stats.run.report',
            tooltip: 'label.stats.run.report',
            className: 'mxs-button--primary',
            disabled: this.isRunReportDisabled,
            clickHandler: this.runReport
        };
        this.exportReportButton = {
            label: 'label.export.to.csv',
            tooltip: 'label.export.to.csv',
            className: 'mxs-button--primary',
            disabled: this.isRunReportDisabled,
            clickHandler: this.exportReport
        };

        of(
            this.financeService.getMerchants(),
            this.transactionsService.getSearchTypes(),
            this.transactionsService.getDateRanges()
        ).pipe(
            (takeUntil(this.destroy$$)), combineLatestAll())
            .subscribe(response => {
                const merchants = response[0] as IdNameMxObj[];
                const searchTypes = response[1] as TransactionsSearchTypeObj[];
                const dateRanges = response[2] as IdNameMxObj[];

                this.merchants.push(...merchants
                    .map(m => ({
                        key: m.id,
                        value: m.name,
                        dependOn: null
                    }) as KeyValueObj));
                this.transactionTypes.push(...searchTypes
                    .map(t => ({
                        key: t.id,
                        value: t.name,
                        dependOn: null
                    }) as KeyValueObj));
                this.dateRanges.push(...dateRanges
                    .map(d => ({
                        key: d.id,
                        value: d.name,
                        dependOn: null
                    }) as KeyValueObj));

                this.selectedMerchantControl.setValue(this.merchants[0]);
                this.selectedTransactionTypeControl.setValue(this.transactionTypes[0]);
                this.selectedDateRangeControl.setValue(this.dateRanges[0]);
                this.gettingData = false;
                this.runReport();
            });
        this.gridColumns = [
            new GridColumn({
                field: 'updatedOn',
                label: () => this.translateService.getTranslatedLabel('label.finance.transactions.date'),
                sortable: true,
                hidden: false
            }),
            new GridColumn({
                field: 'period',
                label: () => this.translateService.getTranslatedLabel('label.finance.transactions.period'),
                sortable: true,
                hidden: false
            }),
            new GridColumn({
                field: 'merchant',
                label: () => this.translateService.getTranslatedLabel('label.finance.transactions.merchant'),
                sortable: true,
                hidden: !this.balancePerMerchant
            }),
            new GridColumn({
                field: 'type',
                label: () => this.translateService.getTranslatedLabel('label.finance.transactions.type'),
                sortable: true,
                hidden: false
            }),
            new GridColumn({
                field: 'credit',
                label: () => this.translateService.getTranslatedLabel('label.finance.transactions.credit'),
                sortable: true,
                hidden: false
            }),
            new GridColumn({
                field: 'debit',
                label: () => this.translateService.getTranslatedLabel('label.finance.transactions.debit'),
                sortable: true,
                hidden: false
            }),
            new GridColumn({
                field: 'balance',
                label: () => this.translateService.getTranslatedLabel('label.finance.transactions.balance'),
                sortable: true,
                hidden: !this.isShowBalance
            })
        ];

    }

    ngAfterContentInit(): void {
        this.grid.customize.pipe(first()).subscribe((gridCustomizerEvent: MxGridCustomizerEvent) => {
            gridCustomizerEvent.customizeActions(MxGridButtonsConfig.hideDefault())
                .customizeEmptyGridParams(() => {
                    if (this.firstLoadReport) {
                        return {image: 'ghost_smile', text: 'label.table.nodata.first'};
                    } else {
                        return {image: 'ghost_sad', text: 'label.table.nodata'};
                    }
                })
                .customizeProps({
                    dateFormat: MOMENT_DATE_FORMAT_DAY,
                    selectionMode: 'none'
                })
                .customizeOrder(new MxGridHandleOrderDefault('order_col'));
        });
    }

    onCustomize(event: MxGridCustomizerEvent): void {
        event
            .customizeLoadRowsOnStart(false)
            .customizeProps({
                selectionMode: 'single',
                readonly: true,
                showButtons: true,
                showColumns: false
            })
            .onSetUp(service => {
                this.gridService = service;
            });
    }

    private isRunReportDisabled = (): boolean => {
        return this.form.invalid || this.gettingData;
    };

    private runReport = () => {
        this.gettingData = true;
        of(
            this.transactionsService.getTransactionsFiltered(
                +this.selectedTransactionTypeControl.value[this.key],
                +this.selectedDateRangeControl.value[this.key],
                this.balancePerMerchant ? +this.selectedMerchantControl.value[this.key] : null),
            this.transactionsService.isShowBalance(+this.selectedTransactionTypeControl.value[this.key]),
        ).pipe(
            (takeUntil(this.destroy$$)), withPreloader(this.preloader, this.preloaderConfig), combineLatestAll())
            .subscribe(result => {
                const transactions = result[0] as TransactionPrlObj[];
                const isShowBalance = result[1] as boolean;

                this.updateGrid(transactions, isShowBalance);

                this.gettingData = false;
                this.isDataReady = true;
            });
    };

    private exportReport = () => {
        this.gettingData = true;

        of(
            this.transactionsService.exportTransactions(
                +this.selectedTransactionTypeControl.value[this.key],
                +this.selectedDateRangeControl.value[this.key],
                this.languageService.getLanguage(),
                this.balancePerMerchant ? +this.selectedMerchantControl.value[this.key] : null),
        ).pipe(
            (takeUntil(this.destroy$$)), withPreloader(this.preloader, this.preloaderConfig), combineLatestAll())
            .subscribe(result => {
                const blob = new Blob([result[0].toString()], {type: 'text/csv;charset=utf-8;'});
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.setAttribute('href', url);
                link.setAttribute('download', 'paymentHistory.csv');
                link.click();
                this.gettingData = false;
            });
    };

    private updateGrid = (transactions: TransactionPrlObj[], isShowBalance: boolean) => {
        this.updateGridColumn('merchant', !this.balancePerMerchant);
        this.updateGridColumn('balance', !isShowBalance);

        this.gridService.columns.setColumns([]);
        this.gridService.columns.setColumns(this.gridColumns);
        this.gridService.collection.setRows(transactions);
    };

    private updateGridColumn(name: string, value: boolean) {
        const index = this.gridColumns.findIndex(c => c.prop === name);

        if (index !== -1) {
            this.gridColumns[index].hidden = value;
        }
    };
}
